import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { successAlert, errorAlert } from "../../utils/alert";
import { FaUniversity, FaSpinner, FaLock, FaKey, FaTimes, FaShieldAlt } from "react-icons/fa";

const BankSettings = () => {
  const [settings, setSettings] = useState({
    account_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    branch: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // OTP Modal state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [targetEmail, setTargetEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await API.fetchBankSettings();
      if (res) {
        setSettings({
          account_name: res.account_name || "",
          bank_name: res.bank_name || "",
          account_number: res.account_number || "",
          ifsc_code: res.ifsc_code || "",
          branch: res.branch || "",
        });
      }
    } catch (err) {
      console.error(err);
      errorAlert("Failed to load settings", err?.message || "Could not fetch Bank configurations.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Step 1: Request OTP
  const handleInitiateSave = async (e) => {
    e.preventDefault();
    setSendingOtp(true);
    try {
      const res = await API.sendBankSettingsOtp();
      setTargetEmail(res?.email || "your registered email");
      setCooldown(res?.resend_after || 60);
      setOtp("");
      setShowOtpModal(true);
      successAlert("OTP Sent", `Verification code sent to ${res?.email || "your email"}.`);
    } catch (err) {
      console.error(err);
      errorAlert("Failed to Send OTP", err?.response?.data?.message || err?.message || "Unable to send verification OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Step 2: Confirm OTP & Save
  const handleConfirmOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.trim().length !== 6) {
      errorAlert("Invalid OTP", "Please enter a valid 6-digit numeric OTP code.");
      return;
    }
    setSaving(true);
    try {
      await API.updateBankSettings({
        ...settings,
        otp: otp.trim(),
      });
      setShowOtpModal(false);
      setOtp("");
      successAlert("Bank Details Updated", "Association bank account details successfully updated with OTP verification.");
    } catch (err) {
      console.error(err);
      errorAlert("Update Failed", err?.response?.data?.message || err?.message || "Invalid or expired OTP code.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen bg-slate-100 mobile-page-pad overflow-hidden">
        {/* Glow Effects */}
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-indigo-300/35 blur-3xl hidden sm:block" />
        <div className="pointer-events-none absolute top-40 -left-20 h-72 w-72 rounded-full bg-sky-300/30 blur-3xl hidden sm:block" />
        
        <div className="relative max-w-2xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3.5 mb-8">
            <div className="p-3 bg-indigo-50 text-indigo-750 rounded-2xl shadow-sm border border-indigo-200">
              <FaUniversity size={28} className="text-indigo-650" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">Bank Account Details</h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage the Association's bank transfer coordinates shown to players during paid event registrations.
              </p>
            </div>
          </div>

          <form onSubmit={handleInitiateSave} className="space-y-6">
            <div className="bg-white/90 backdrop-blur rounded-3xl border border-slate-200 shadow-md p-6 sm:p-8 space-y-6">
              {loading ? (
                <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <FaSpinner className="animate-spin text-indigo-600" size={32} />
                  <p className="text-sm font-medium">Fetching bank account details...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs font-medium">
                    <FaShieldAlt className="text-amber-600 shrink-0" size={16} />
                    <span>Security Notice: Changing bank details requires OTP verification sent to your admin email.</span>
                  </div>

                  {/* Account Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Account Holder Name</label>
                    <input
                      type="text"
                      name="account_name"
                      value={settings.account_name}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Para Sports Association of Andhra Pradesh"
                      className="w-full border border-slate-300 rounded-xl p-3 bg-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>

                  {/* Bank Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Bank Name</label>
                    <input
                      type="text"
                      name="bank_name"
                      value={settings.bank_name}
                      onChange={handleChange}
                      required
                      placeholder="e.g. State Bank of India (SBI)"
                      className="w-full border border-slate-300 rounded-xl p-3 bg-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>

                  {/* Account Number */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Account Number</label>
                    <input
                      type="text"
                      name="account_number"
                      value={settings.account_number}
                      onChange={handleChange}
                      required
                      placeholder="e.g. 41029384756"
                      className="w-full border border-slate-300 rounded-xl p-3 bg-white text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>

                  {/* IFSC Code */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">IFSC Code</label>
                    <input
                      type="text"
                      name="ifsc_code"
                      value={settings.ifsc_code}
                      onChange={handleChange}
                      required
                      placeholder="e.g. SBIN0001234"
                      className="w-full border border-slate-300 rounded-xl p-3 bg-white text-sm font-mono focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>

                  {/* Branch Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Branch Name</label>
                    <input
                      type="text"
                      name="branch"
                      value={settings.branch}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Vijayawada Main Branch"
                      className="w-full border border-slate-300 rounded-xl p-3 bg-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Action Button */}
              {!loading && (
                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={sendingOtp}
                    className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold text-sm px-6 py-2.5 rounded-xl shadow transition-all cursor-pointer"
                  >
                    {sendingOtp ? (
                      <>
                        <FaSpinner className="animate-spin" size={14} />
                        Sending Verification OTP...
                      </>
                    ) : (
                      <>
                        <FaLock size={14} />
                        Send OTP & Update Bank Details
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 relative animate-in fade-in zoom-in-95">
            <button
              onClick={() => setShowOtpModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-all"
            >
              <FaTimes size={18} />
            </button>

            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-inner border border-indigo-100">
                <FaKey size={26} />
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900">OTP Security Verification</h3>
                <p className="text-xs text-slate-500 mt-1">
                  We sent a 6-digit OTP code to <span className="font-semibold text-slate-800">{targetEmail}</span> to confirm this bank account change.
                </p>
              </div>

              <form onSubmit={handleConfirmOtp} className="w-full space-y-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">Enter 6-Digit Verification Code</label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="w-full text-center text-2xl font-mono tracking-widest border border-slate-300 rounded-2xl py-3 focus:ring-2 focus:ring-indigo-500 outline-none bg-slate-50 font-bold"
                    required
                    autoFocus
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                  <span>Didn't receive code?</span>
                  <button
                    type="button"
                    disabled={cooldown > 0 || sendingOtp}
                    onClick={handleInitiateSave}
                    className="text-indigo-600 font-bold hover:underline disabled:text-slate-400"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={saving || otp.length !== 6}
                  className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-bold text-sm rounded-xl shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="animate-spin" size={16} />
                      Verifying & Saving...
                    </>
                  ) : (
                    "Verify OTP & Update Details"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BankSettings;
